import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Card, Drawer, Grid, IconButton, Table, TableCell, TableHead, TableRow, Typography, Skeleton, Popover, Tooltip } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useDispatch } from "react-redux";
import EditIcon from "@mui/icons-material/Edit";
import moment from "moment";
import { setRevisonHistory } from "../actions";
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { useParams } from "react-router";
import InfiniteScroll from "react-infinite-scroll-component";
import { globalGetService } from "../../utils/globalApiServices";
import { gaTelesisInstance } from "..";

const DEFAULT_PAGE_SIZE = 50;

const toCamelCase = (str) => {
  if (!str) return "";
  const cleanedStr = str.replace(/_?id$/i, ""); 
  return cleanedStr
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

const RESERVE_PBH = [
  { key: "range_label", label: "Range" },
  { key: "min", label: "Min" },
  { key: "max", label: "Max" },
  { key: "rate", label: "Rate" },
  { key: "esc_percentage", label: "Esc %" },
  { key: "esc_date", label: "Esc Date" },
  { key: "rental_rate_type", label: "Rate Type" },
];
const RENTAL_PBH = [
  { key: "range_condition", label: "Range Condition" },
  { key: "min_value", label: "Min" },
  { key: "max_value", label: "Max" },
  { key: "rate", label: "Rate" },
  { key: "esc_percentage", label: "Esc %" },
  { key: "esc_date", label: "Esc Date" },
  { key: "rate_type", label: "Rate Type" },
];
// interval_condition rows can have any shape (rate/ratio, rate/derate, nested "ratios" arrays, etc.)
// and old/new can each use different keys, so columns are derived from the actual data instead of a fixed list.
const buildDynamicColumns = (rows) => {
  if (!Array.isArray(rows)) return [];
  const keys = [];
  rows.forEach((row) => {
    if (row && typeof row === "object" && !Array.isArray(row)) {
      Object.keys(row).forEach((key) => {
        if (key === "type") return; // not shown in revision history
        if (!keys.includes(key)) keys.push(key);
      });
    }
  });
  return keys.map((key) => ({ key, label: toCamelCase(key) }));
};
const ENGINE_THRUST_TYPES_COLUMNS = [
  { key: "name", label: "Name" },
];
const CUSTOM_PAYMENT_COLUMNS = [
  { key: "period", label: "Calculation Period" },
  { key: "payment_date", label: "Payment Date" },
  { key: "rent", label: "Rent" },
  { key: "basic_rent", label: "Basic Rent" },
  { key: "reference_value", label: "Reference Value" },
];
const INSURANCE_VARIABILITY_COLUMNS = [
  { key: "start_date", label: "Start Date" },
  { key: "end_date", label: "End Date" },
  { key: "amount", label: "Amount" },
  { key: "variability", label: "Variablity" },
];


const PBH_CODE_LABEL_MAPS = {
  range_condition: { 1: "Less Than Equal To", 2: "In Between Equal To", 3: "Greater Than Equal To" },
  rate_type: { 1: "Fixed", 2: "Per Unit" },
};

const formatPbhConditionCell = (row, key) => { // handle for BOTH Reserve and Rental, plus dynamic interval condition
  const value = row?.[key];
  if (value === null || value === undefined || value === "") return "-";
  if (key === "esc_date" || key === "payment_date" || key === "start_date" || key === "end_date") return moment(value).format("DD MMM YYYY");
  if (Array.isArray(value)) {
    if (value.length === 0) return "-";
    const isObjectRows = value.every((item) => item && typeof item === "object" && !Array.isArray(item));
    if (!isObjectRows) return value.join(", ");
    const nestedColumns = buildDynamicColumns(value);
    return (
      <Table size="small" className="mui-table-format">
        <TableHead>
          <TableRow>
            {nestedColumns.map((col) => (
              <TableCell key={col.key}>{col.label}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <tbody>
          {value.map((nestedRow, idx) => (
            <TableRow key={idx}>
              {nestedColumns.map((col) => (
                <TableCell key={col.key}>{formatPbhConditionCell(nestedRow, col.key)}</TableCell>
              ))}
            </TableRow>
          ))}
        </tbody>
      </Table>
    );
  }
  if (typeof value === "object") return value?.label || "-";
  if (PBH_CODE_LABEL_MAPS[key]) return PBH_CODE_LABEL_MAPS[key][value] ?? value;
  return value;
};

const PbhConditionCell = ({ value, type, columns = RESERVE_PBH }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const rows = Array.isArray(value) ? value : [];
  const open = Boolean(anchorEl);

  return (
    <>
      <Tooltip title={type === "old" ? "Click to show Old value" : "Click to show New value"}>
        <IconButton size="small" onClick={(e) => setAnchorEl(e.currentTarget)}>
          <InfoOutlinedIcon fontSize="small" color="primary" />
        </IconButton>
      </Tooltip>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Box sx={{ p: 1, maxWidth: 560, overflowX: "auto" }}>
          <Table size="small" className="mui-table-format" stickyHeader>
            <TableHead>
              <TableRow>
                {columns?.map((col) => (
                  <TableCell key={col.key}>{col.label}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <tbody>
              {rows?.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={columns?.length} align="center">
                    No records found
                  </TableCell>
                </TableRow>
              ) : (
                rows?.map((row, idx) => (
                  <TableRow key={idx}>
                    {columns?.map((col) => (
                      <TableCell key={col.key}>{formatPbhConditionCell(row, col.key)}</TableCell>
                    ))}
                  </TableRow>
                ))
              )}
            </tbody>
          </Table>
        </Box>
      </Popover>
    </>
  );
};

const RevisionHistoryHeader = ({ showOldValue, item }) => (
  <TableHead>
    <TableRow>
      <TableCell sx={{minWidth:'30%'}}>Label</TableCell>
      {showOldValue && <TableCell>Old Value</TableCell>}
      <TableCell>{item?.action?.label==='delete'?'Deleted':'New'} Value</TableCell>
    </TableRow>
  </TableHead>
);

const RevisionHistoryList = ({ item, showOldValue,moduleName }) => {
const formatValue = (value, label) => {
  if (value === null || value === undefined || value === "") return "-";
  if ( Array.isArray(value) ){
    let val = value.map(i=> i?.label || i?.value || '--')?.toString()
    return val ?? '--'
  }
  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }
  if (typeof value === "object") {
    if (value?.label) return value.label;
    if (value?.name) return value.name;
    return "-"; 
  }
   if (typeof value === "string" && isNaN(value) && moment(value, moment.ISO_8601, true).isValid()) {
   return label === "Updated At"
    ? moment(value).format("DD MMM YYYY, hh:mm A")
    : moment(value).format("DD MMM YYYY");
  }
  return value;
};

  const isReservePbhCondition = moduleName === "reserve_rates" && item?.field === "pbh_condition";
  const isLeasePbhCondition = moduleName === "lease_rental_rate" && item?.field === "pbh_conditions";
  const isPbhCondition = isReservePbhCondition || isLeasePbhCondition;
  const isIntervalCondition = (moduleName === "reserve_rates" || moduleName === "lease_rental_rate") && item?.field === "interval_condition";
  const isEngineThrustTypes = moduleName === "reserve_rates" && item?.field === "engine_thrust_types";
  const isCustomPayment = moduleName === "lease_rental_rate" && item?.field === "custom_payments";

 const isVariability = moduleName === "insurance" && item?.field === "variabilities";
  const isConditionType = isPbhCondition || isIntervalCondition || isEngineThrustTypes || isCustomPayment || isVariability;
  const staticConditionColumns = isEngineThrustTypes
    ? ENGINE_THRUST_TYPES_COLUMNS
    : isLeasePbhCondition
      ? RENTAL_PBH
      : isCustomPayment
        ? CUSTOM_PAYMENT_COLUMNS
        :isVariability
            ? INSURANCE_VARIABILITY_COLUMNS
            : RESERVE_PBH;;

  const oldConditionColumns = isIntervalCondition ? buildDynamicColumns(item?.old) : staticConditionColumns;
  const newConditionColumns = isIntervalCondition ? buildDynamicColumns(item?.new) : staticConditionColumns;

  return (
    <TableRow>
      <TableCell>{item?.field === "contractual_start_date" ? (gaTelesisInstance ? "Lease Signature Date" : "Contract Date") : toCamelCase(item?.field)}</TableCell>
      {showOldValue && (
        <TableCell>
          {isConditionType ? <PbhConditionCell value={item?.old} type="old" columns={oldConditionColumns} /> : formatValue(item?.old, item?.field)}
        </TableCell>
      )}
      <TableCell>
        {isConditionType ? <PbhConditionCell value={item?.new} type="new" columns={newConditionColumns} /> : formatValue(item?.new, item?.field)}
      </TableCell>
    </TableRow>
  );
};

const buildQueryString = (query = {}) => {
  const params = new URLSearchParams();
  Object.entries(query).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;
    params.set(key, value);
  });
  const queryString = params.toString();
  return queryString ? `?${queryString}` : "";
};

const RevisionHistory = ({ onToggleModal, moduleName, asset_type,query, asset_slug, revisionHistory, section_type }) => {
  const params =useParams()
  const dispatch = useDispatch();
  const [expandedIndex, setExpandedIndex] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [historyItems, setHistoryItems] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const requestIdRef = useRef(0);

  const type = useMemo(() => (section_type ? { section_type } : {}), [section_type]);
  const handleToggle = (index) => setExpandedIndex(expandedIndex === index ? null : index);

  const buildHistoryUrl = (pageToLoad = 1, perPage = DEFAULT_PAGE_SIZE) => {
    const pageQuery = buildQueryString({ page: pageToLoad, per_page: perPage });

    if (section_type === "delivery" || section_type === "re-delivery") {
      const deliveryQuery = buildQueryString({ delivery_assembly_type: section_type, page: pageToLoad, per_page: perPage });
      if (revisionHistory?.data?.type === "global") {
        return `/audit-logs/lease/${params?.leaseSlug}/history/${moduleName}${deliveryQuery}`;
      }
      if (revisionHistory?.tableName === "event") {
        return `audit-logs/delivery_redelivery_maintenance_event/${revisionHistory?.data?.slug}${deliveryQuery}`;
      }
      return `audit-logs/delivery_redelivery_assembly/${revisionHistory?.data?.slug}${deliveryQuery}`;
    }
    if (revisionHistory?.data?.type === "asset") {
      return `/audit-logs/global/history/${moduleName}${buildQueryString({ asset_type, asset_slug, page: pageToLoad, per_page: perPage })}`;
    }
    if (revisionHistory?.data?.slug) {
      if (revisionHistory?.data?.type === "global") {
        return `/audit-logs/lease/${revisionHistory?.data?.slug}/history/${moduleName}${pageQuery}`;
      }
      return `/audit-logs/${moduleName}/${revisionHistory?.data?.slug}${buildQueryString({
        ...type,
        page: pageToLoad,
        per_page: perPage,
      })}`;
    }
    return `/audit-logs/global/history/${moduleName}${buildQueryString({ page: pageToLoad, per_page: perPage })}`;
  };

  const fetchHistory = async ({ pageToLoad = 1, append = false, perPage = pageSize } = {}) => {
    const requestId = ++requestIdRef.current;

    if (append) {
      setLoadingMore(true);
    } else {
      setLoading(true);
      setHistoryItems([]);
      setPagination(null);
      setExpandedIndex(null);
    }

    try {
      const response = await globalGetService(buildHistoryUrl(pageToLoad, perPage));
      if (requestId !== requestIdRef.current) return;

      const normalized = response?.data?.data;
      const nextList = normalized.list || [];
      const nextPagination = normalized.pagination || null;

      setHistoryItems((prev) => (append ? [...prev, ...nextList] : nextList));
      setPagination(nextPagination);
      setCurrentPage(pageToLoad);
      setPageSize(nextPagination?.per_page || perPage || DEFAULT_PAGE_SIZE);
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
        setLoadingMore(false);
      }
    }
  };

  useEffect(() => {
    if (!revisionHistory?.data && !revisionHistory?.modal) return;

    requestIdRef.current += 1;
    setExpandedIndex(null);
    setHistoryItems([]);
    setPagination(null);
    setCurrentPage(1);
    setPageSize(DEFAULT_PAGE_SIZE);
    fetchHistory({ pageToLoad: 1, append: false, perPage: DEFAULT_PAGE_SIZE });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [revisionHistory?.data?.slug, revisionHistory?.data?.type, revisionHistory?.tableName, moduleName, asset_type, asset_slug, section_type]);

  const hasMore = useMemo(() => {
    if (loading || loadingMore) return false;
    if (pagination?.total !== undefined && pagination?.total !== null) {
      return historyItems.length < pagination.total;
    }
    return historyItems.length > 0 && historyItems.length % pageSize === 0;
  }, [historyItems.length, loading, loadingMore, pageSize, pagination?.total]);

  const loadMore = () => {
    if (loading || loadingMore || !hasMore) return;
    fetchHistory({ pageToLoad: currentPage + 1, append: true, perPage: pageSize });
  };
  const handleClose = () => {
    dispatch(setRevisonHistory());
    onToggleModal();
  };
 console.log("revisionHistory-->",revisionHistory,moduleName)
  return (
    <Drawer anchor="right" open={true} sx={{ "& .MuiDrawer-paper": { width: 600 } }}>
      <Box sx={{ p: 2, borderBottom: "1px solid #eee", position: "sticky", top: 0, backgroundColor: "#fff", zIndex: 7, }}>
        <Grid container alignItems="center">
          <Grid item size={{ md: 6, xs: 12 }}>
            <Typography variant="h6" color="primary">Revision History</Typography>
          </Grid>
          <Grid item size={{ md: 6, xs: 12 }} sx={{ textAlign: "right" }}>
            <IconButton onClick={handleClose}><CloseIcon /></IconButton>
          </Grid>
        </Grid>
      </Box>
      <Box sx={{ position: "relative", pl: 5, pb: 0, pr: 2, mt: "25px", zIndex: 1 }}>
        <Box sx={{ position: "absolute", left: 32, top: 0, bottom: 0, width: "2px", bgcolor: loading ? "rgba(0, 0, 0, 0.11)" : "primary.main", zIndex: 1 }} />
        {loading ? (
          <>
            {[1, 2, 3].map((i) => (
              <Box key={i} sx={{ display: "flex", alignItems: "stretch", mb: 3 }}>
                <Box sx={{ position: "relative", minWidth: "15px", height: "30px", top: "-3px", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 2, marginLeft: -2.6 }}>
                  <Skeleton variant="circular" width={30} height={30} />
                </Box>
                <Card sx={{ flex: 1, marginLeft: 2, borderRadius: "12px", border: "1px solid rgba(0, 0, 0, 0.12)", boxShadow: "none", p: 2 }}>
                  <Grid container spacing={2}>
                    <Grid item size={{ md: 6, xs: 12 }}>
                      <Skeleton width="80%" height={20} />
                      <Skeleton width="60%" height={20} />
                    </Grid>
                    <Grid item size={{ md: 6, xs: 12 }}>
                      <Skeleton width="80%" height={20} />
                      <Skeleton width="60%" height={20} />
                    </Grid>
                    <Grid item size={{ md: 6, xs: 12 }}>
                      <Skeleton width="40%" height={20} />
                    </Grid>
                  </Grid>
                </Card>
              </Box>
            ))}
          </>
        ) : historyItems?.length === 0 ? (
          <Typography variant="body2" color="text.secondary">No revision history available</Typography>
        ) : (
           <InfiniteScroll
            dataLength={historyItems.length}
            next={loadMore}
            hasMore={hasMore}
            loader={<h4 style={{ clear: "both", textAlign: "center", padding: "10px" }}>Loading...</h4>}
            endMessage={pagination?.total===historyItems?.length ? <p style={{ clear: "both", textAlign: "center", padding: "10px" }}>Yay! You have seen it all</p> : null}
            scrollableTarget="revision-history-scroll-container"
            style={{ overflow: "visible", background: "transparent" }}
          >
          {historyItems?.map((item, index) => {
            const logHistory = Object.entries(item?.changes || {}).map(([key, value]) =>
              item?.action?.label === "create"
                ? { field: key, old: null, new: value }
                : item?.action?.label === "delete"
                  ? { field: key, old: null, new: value }
                  : { field: key, old: value?.old, new: value?.new }
            );

            const showOldValue = item?.action?.label !== "create" && item?.action?.label !== "delete";
            
            return (
              <Box key={index} sx={{ display: "flex", alignItems: "stretch", mb: index < historyItems.length - 1 ? 1 : 5 }}>
                <Box sx={{ position: "relative", minWidth: "15px", height: "30px", top: "-5px", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 2 }}>
                  {item?.action?.label === "create"
                    ? <AddIcon sx={{ color: "primary.main", bgcolor: "white", border: "2px solid", borderColor: "primary.main", borderRadius: "50%", fontSize: "20px", boxShadow: 1, zIndex: 2, marginLeft: "-30px", backgroundClip: "padding-box" }} />
                    : item?.action?.label === "delete"
                      ? <Box sx={{ width: "1em", height: "1em", fontSize: "20px", display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "white", border: "2px solid", borderColor: "primary.main", borderRadius: "50%", boxShadow: 1, zIndex: 2, marginLeft: "-30px", backgroundClip: "padding-box" }}>
                          <DeleteIcon color='error' sx={{ fontSize: "19px" }} />
                        </Box>
                      : <Box sx={{ width: "1em", height: "1em", fontSize: "20px", display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "white", border: "2px solid", borderColor: "primary.main", borderRadius: "50%", boxShadow: 1, zIndex: 2, marginLeft: "-30px", backgroundClip: "padding-box" }}>
                          <EditIcon sx={{ color: "primary.main", fontSize: "15px" }} />
                        </Box>}
                </Box>
                <Card sx={{ flex: 1, borderRadius: "12px", border: "1px solid rgba(0, 0, 0, 0.12)", boxShadow: "none", p: 2 }}>
                  <Grid container spacing={2}>
                    <Grid item size={{ md: item?.category ? 4 : 6, xs: 12 }}>
                      <Typography variant="body2" color="text.secondary">
                        {item?.action?.label === "create" ? "Created at" : item?.action?.label === "delete" ? "Deleted at" : "Updated at"}
                      </Typography>
                      <Typography variant="body2" color="rgb(0, 0, 0)" className="revision-history-content">{moment(item?.timestamp).format("DD MMM YYYY, hh:mm A")}</Typography>
                      <Typography variant="body1">{item.createdAt}</Typography>
                    </Grid>
                    {
                     item?.category &&
                     <Grid item size={{ md: 4, xs: 12 }}>
                      <Typography variant="body2" color="text.secondary">
                        {"Category"}
                      </Typography>
                      <Typography variant="body2" color="rgb(0, 0, 0)" className="revision-history-content">{item?.category}</Typography>
                      <Typography variant="body1">{item.createdAt}</Typography>
                     </Grid>
                    }
                    <Grid item size={{ md:  item?.category ? 4 : 6, xs: 12 }} sx={{ display: "flex", flexDirection: "column", justifyContent: "flex-end", alignItems: "flex-end" }}>
                      <Typography variant="body2" color="text.secondary">
                        {item?.action?.label === "create" ? "Created by" : item?.action?.label === "delete" ? "Deleted by" : "Updated by"}
                      </Typography>
                      <Typography variant="body2" color="rgb(0, 0, 0)" className="revision-history-content">{item?.user?.name}</Typography>
                      <Typography variant="body1">{item.createdBy}</Typography>
                    </Grid>
                    <Grid item size={{ md: 6, xs: 12 }}>
                      <Box sx={{ display: "flex", alignItems: "center", cursor: "pointer" }} onClick={() => handleToggle(index)}>
                        <Typography variant="body2" color="primary">{expandedIndex === index ? "Hide Data" : "Show Data"}</Typography>
                        <ExpandMoreIcon sx={{ fontSize: 18, ml: 0.5, color: "primary.main", transform: expandedIndex === index ? "rotate(180deg)" : "rotate(0deg)", transition: "0.2s" }} />
                      </Box>
                    </Grid>
                    {expandedIndex === index && (
                      <Grid item size={{ md: 12, xs: 12 }}>
                        {(moduleName === "utilization" || moduleName === "utilization_master") && item?.utilization_asset ?   <Typography display="flex" variant="body2" color="text.secondary">Asset : <Typography  sx={{ml:"2px"}} variant="body2" color="rgb(0, 0, 0)" className="revision-history-content">{item?.utilization_asset}</Typography></Typography> : null }
                        <Box sx={{ mt: 1, p: 2, backgroundColor: "rgb(246, 246, 246)", borderRadius: "8px" }}>
                          <Table className="mui-table-format" stickyHeader>
                            <RevisionHistoryHeader showOldValue={showOldValue} item={item} />
                            <tbody>
                              {logHistory.map((historyItem, idx) => (
                                <RevisionHistoryList key={idx} item={historyItem} showOldValue={showOldValue} moduleName={moduleName} />
                              ))}
                            </tbody>
                          </Table>
                        </Box>
                      </Grid>
                    )}
                  </Grid>
                </Card>
              </Box>
            );
          })
        }</InfiniteScroll>)}
        {loadingMore && !loading ? <Box sx={{ textAlign: "center", py: 2 }}>Loading...</Box> : null}
      </Box>
    </Drawer>
  );
};

export default RevisionHistory;
